/**
 * bossConstants.ts
 * Tunable constants for World 2 boss — the **Void Mothership**.
 *
 * Scope (this file): hitbox, HP, entry/hold kinematics, the trigger
you  * time the spawner waits for before bringing the boss in, and the
 * Phase-1 behaviours: turret sweeps (`bossTurret.ts`) and drone spawns
 * (`bossDroneSpawner.ts`). The Phase-2 shield cycle (`bossShield.ts`)
 * lives in `bossPhase2Constants.ts` and the Phase-3 ram-charge
 * (`bossRam.ts`) + homing missile barrages (`bossMissileBarrage.ts`)
 * live in `bossPhase3Constants.ts`; both are re-exported below so
 * `./bossConstants` remains the single import surface.
 *
 * NO React imports.
 *
 * Phase 4 — World 2: Starfield Drift (boss complete).
 *
 * @module bossConstants
 */

// ── Void Mothership hitbox ─────────────────────────────────────────────

/**
 * Capital-ship hitbox width in px. Significantly wider than any drone
 * (fighter 36, bomber 48, satellite 40) so the boss reads as "capital
 * scale" against the ship + viewport at a glance.
 */
export const VOID_MOTHERSHIP_WIDTH = 120;

/** Capital-ship hitbox height in px. Tall, but leaves clear sky above
 *  and a runway below so the ship can strafe past on the vertical axis. */
export const VOID_MOTHERSHIP_HEIGHT = 96;

// ── Combat ─────────────────────────────────────────────────────────────

/**
 * Boss HP — tuned up from the original ROADMAP 8 HP after live testing
 * the W2 boss felt too cheap: 24 hits at the current 1-dmg-per-projectile
 * rate gives a ~3.6 s minimum kill window with the 0.15 s cooldown, so
 * the player now has to survive at least one full ATTACK→VOLLEY rotation
 * before the boss falls. Three-phase headroom for the eventual
 * weak-point + telegraph passes — increase further if the fight still
 * resolves before the second BARRAGE volley lands.
 */
export const VOID_MOTHERSHIP_HP = 24;

// ── Entry + hold kinematics ────────────────────────────────────────────

/**
 * Speed (px / s, world-space) at which the boss drifts left during its
 * ENTRY state. The boss is moving LEFT in world space WHILE the camera
 * is also moving right — apparent on-screen speed is therefore
 * `VOID_MOTHERSHIP_ENTRY_SPEED + SPACE_AUTO_SCROLL_SPEED`. Slow enough
 * that the boss telegraphs "something big is arriving" rather than
 * snapping into place.
 */
export const VOID_MOTHERSHIP_ENTRY_SPEED = 60;

/**
 * Camera-relative X the boss holds at once it finishes its ENTRY drift.
 * Anchors near the right edge of the viewport, leaving the left two-
 * thirds clear for the ship to manoeuvre. Boss world-X each tick is
 * `cameraX + VOID_MOTHERSHIP_HOLD_X_OFFSET` while in HOLD.
 */
export const VOID_MOTHERSHIP_HOLD_X_OFFSET = 360;

/**
 * Spawn camera-relative X offset. Boss spawns just past the right edge
 * of the viewport (viewport width 480 + margin) so the first few ticks
 * of ENTRY are off-screen and the boss visibly slides in.
 */
export const VOID_MOTHERSHIP_SPAWN_X_OFFSET = 540;

/**
 * Camera-relative Y the boss holds at. Vertical centre of the viewport,
 * leaving symmetric strafing room above and below. During HOLD the
 * boss bobs vertically about this anchor — see
 * {@link VOID_MOTHERSHIP_BOB_AMPLITUDE_PX} /
 * {@link VOID_MOTHERSHIP_BOB_FREQ_HZ}.
 */
export const VOID_MOTHERSHIP_HOLD_Y = 56;

/**
 * Peak vertical displacement (px) of the boss's HOLD bob, measured
 * from {@link VOID_MOTHERSHIP_HOLD_Y}. Sized so the bob is clearly
 * visible at game scale without pushing the boss into the wave-
 * telegraph or HUD strips. Combines with {@link VOID_MOTHERSHIP_BOB_FREQ_HZ}
 * to make the capital ship read as floating rather than stationary,
 * and shifts turret / missile launch geometry frame-to-frame so the
 * player can't precompute a juke pattern.
 */
export const VOID_MOTHERSHIP_BOB_AMPLITUDE_PX = 16;

/**
 * Bob frequency (cycles per second). Slow enough that the motion
 * reads as a heavy capital ship drifting in zero-g, fast enough that
 * the player perceives it as ongoing motion rather than a static
 * hover. One full sine cycle every ~5 s.
 */
export const VOID_MOTHERSHIP_BOB_FREQ_HZ = 0.2;

/**
 * Lateral (X) sweep amplitude during HOLD, in pixels. Combined with
 * the Y bob this produces a Lissajous-shaped path so the boss is
 * never reliably at the same screen-space coordinate two seconds
 * apart.
 *
 * DECISION (W2 overhaul): Original boss HOLD was a Y-only bob of
 * ±16 px — the player could camp at the boss's column and let it
 * walk into their fire. Adding ±56 px of X sweep forces the player
 * to either lead the boss or close the gap during EXPOSED windows.
 */
export const VOID_MOTHERSHIP_SWEEP_AMPLITUDE_PX = 56;

/**
 * Lateral sweep frequency (Hz). Chosen to be a non-integer multiple
 * of {@link VOID_MOTHERSHIP_BOB_FREQ_HZ} so the X / Y phases never
 * resync to a static target — the Lissajous never closes on the same
 * point twice in a HOLD window.
 */
export const VOID_MOTHERSHIP_SWEEP_FREQ_HZ = 0.13;

// ── Trigger ────────────────────────────────────────────────────────────

/**
 * Seconds of `spaceState.elapsedSeconds` after which the boss is
 * triggered. Greybox value chosen so the boss arrives well into the
 * regular spawn waves rather than at level start. Later slices may
 * replace this with a "level-end marker" once authored levels exist.
 */
export const VOID_MOTHERSHIP_TRIGGER_TIME_S = 30;

// ── Phase 1: turret fire ───────────────────────────────────────────────

/**
 * Seconds between successive turret shots once the boss is in HOLD.
 * Greybox cadence — tuned long enough that the player can read the
 * pattern but short enough to feel like real pressure on top of the
 * regular spawn waves. Drone-spawn cadence is intentionally a
 * follow-up slice; only turret fire ships in this PR.
 */
export const VOID_MOTHERSHIP_TURRET_COOLDOWN_S = 1.5;

/**
 * Initial delay (s) between reaching HOLD and the first turret shot.
 * Gives the player a beat to register "boss has stopped" before the
 * first projectile streams out, instead of firing on the same tick
 * the ENTRY→HOLD transition flips.
 */
export const VOID_MOTHERSHIP_TURRET_RAMP_IN_S = 1.0;

/**
 * Speed (px / s, world-space) at which a boss projectile travels left.
 * Faster than the boss's ENTRY drift (60) and well above the bomber's
 * approach speed so a turret bolt cannot be out-ran by holding still.
 */
export const VOID_MOTHERSHIP_PROJECTILE_SPEED = 240;

/** Boss projectile hitbox width in px. Reads as a horizontal bolt. */
export const VOID_MOTHERSHIP_PROJECTILE_WIDTH = 16;

/** Boss projectile hitbox height in px. */
export const VOID_MOTHERSHIP_PROJECTILE_HEIGHT = 6;

/**
 * Hard cap on simultaneously live boss projectiles. Caps the pool size
 * and gives the pool acquire a finite ceiling. Sized to comfortably
 * exceed `ceil((viewport / projectile speed) / cooldown)` so the cap
 * is the *worst* greybox cadence and never the typical one.
 */
export const VOID_MOTHERSHIP_MAX_BOSS_PROJECTILES = 16;

// ── Phase 1: drone-spawn waves ─────────────────────────────────────────

/**
 * Seconds between successive boss-spawned fighter drones once the boss
 * is in HOLD. Slower than the turret cadence (1.5 s) so the player isn't
 * forced to handle two new pressures on the same tick. Sits on top of
 * the regular `spaceEnemySystem` spawn stream — the global
 * `SPACE_ENEMY_MAX_ACTIVE` cap still gates the world's enemy budget.
 * When that cap (or the fighter pool) is saturated the boss follows the
 * pool-miss-no-burn convention: the cooldown is NOT reloaded, so the
 * spawn is retried on the next tick and fires as soon as capacity
 * frees up — the cooldown only governs the spacing between *successful*
 * spawns.
 */
export const VOID_MOTHERSHIP_DRONE_SPAWN_COOLDOWN_S = 3.5;

/**
 * Initial delay (s) between reaching HOLD and the first drone spawn.
 * Set later than `VOID_MOTHERSHIP_TURRET_RAMP_IN_S` (1.0) so the player
 * reads the turret bolt first and then the wave second — two staged
 * tells rather than a single overwhelming opener on the HOLD beat.
 */
export const VOID_MOTHERSHIP_DRONE_SPAWN_RAMP_IN_S = 2.0;

/**
 * Horizontal offset (px) from the boss's LEFT edge at which a spawned
 * drone is placed. Positive values nudge the drone further left so it
 * doesn't visibly pop out of the boss hitbox.
 */
export const VOID_MOTHERSHIP_DRONE_SPAWN_X_OFFSET = 24;

/**
 * Maximum vertical jitter (px, ±) applied to a spawned drone's Y around
 * the boss's centre. Keeps successive spawns from stacking on a single
 * horizontal line. Bounded conservatively so the resulting Y never
 * pushes the drone past the floor inset enforced by the regular
 * spawner.
 */
export const VOID_MOTHERSHIP_DRONE_SPAWN_Y_JITTER = 24;

// ── Phase 2: shield timed-window cycle ─────────────────────────────────
// Extracted to `bossPhase2Constants.ts` to keep this file under the
// 300-line budget. Re-exported here so existing imports from
// `./bossConstants` continue to resolve without churn.
export * from './bossPhase2Constants';

// ── Phase 3: ram charge + homing missile barrage ───────────────────────
// Extracted to `bossPhase3Constants.ts` to keep this file under the
// 300-line budget. Re-exported here so existing imports from
// `./bossConstants` continue to resolve without churn.
export * from './bossPhase3Constants';


